'use strict';

const {
	locationStates,
	locationDetailsStates,
	locationInsightStates
} = require('./states/locationStates.js');
const { userStates } = require('./states/userStates.js');
const { claimStates } = require('./states/claimStates.js');
const { constructElmFunctions } = require('./elmToAngular.js');
const { apiStates } = require('./states/apiStates.js');
const { insightsStates } = require('./states/insightsStates.js');
const { networksStates } = require('./states/networksStates.js');
const { workerLogStates } = require('./states/workerLogStates.js');
const {
	assetsStates,
	mediaStates,
	mediaApprovalStates
} = require('./states/mediaAndAssetStates.js');
const { accountStates } = require('./states/accountStates.js');
const { conflictStates } = require('./states/conflictStates.js');
const { legalDocStates } = require('./states/legalDocStates.js');

const getAllStates = window => {
	const { elm, elmWithFilter } = constructElmFunctions(window.navigator, window.pmtEnv);

	return [
		...userStates({ elm, elmWithFilter, navigator: window.navigator }),
		...locationStates({ elm, elmWithFilter }),
		...locationDetailsStates({ elm, elmWithFilter }),
		...locationInsightStates({ elm, elmWithFilter }),
		...claimStates({ elm, elmWithFilter }),
		...apiStates({ elm, elmWithFilter }),
		...conflictStates({ elm, elmWithFilter }),
		...insightsStates({ elm, elmWithFilter }),
		...networksStates({ elm, elmWithFilter }),
		...workerLogStates({ elm, elmWithFilter }),
		...assetsStates({ elm, elmWithFilter, angular: window.angular }),
		...mediaStates({ elm, elmWithFilter }),
		...accountStates({ elm, elmWithFilter }),
		...legalDocStates({ elm, elmWithFilter }),
		...mediaApprovalStates({ elm, elmWithFilter }),
		{
			name: 'site.Notifications',
			url: '/notifications',
			template: '<pmt-notification-archive></pmt-notification-archive>'
		},
		{
			name: 'site.Tasks',
			url: '/claim/tasks',
			templateUrl: 'components/tasks/tasksView.html',
			controller: 'TasksCtrl',
			requiredPermission: 'permission/task.view'
		},
		elm({
			name: 'site.DashboardV2',
			moduleName: 'DashboardV2.Main',
			url: '/dashboard',
			requiredPermission: 'permission/task.view',
			pageTitle: 'listings.navigation.dashboard.dashboard',
			argsResolver: [
				'pmtEnvService',
				'$stateParams',
				'auth',
				'pmtEnabledNetworksService',
				(pmtEnvService, $stateParams, auth, pmtEnabledNetworksService) => {
					return auth.getUser($stateParams.site).then(user => {
						const site = user.selectedSite;
						const account = {
							name: site.name,
							displayName: site.displayName,
							features: {
								allowFacebookCustomName:
									(site.places.feature.allowFacebookCustomName &&
										site.places.feature.allowFacebookCustomName.enabled) === true,
								allowGoogleCustomName:
									(site.places.feature.allowGoogleCustomName &&
										site.places.feature.allowGoogleCustomName.enabled) === true
							},
							networks: pmtEnabledNetworksService.enabledNetworks(site)
						};
						return {
							siteName: $stateParams.site || $stateParams.siteName,
							account: account,
							today: new Date().toISOString(),
							insightsUrl: pmtEnvService.insightsApiUrl,
							analytics: auth.user?.selectedSite?.analytics,
							apiEnabled: site.places.api.enabled,
							mediaReviewEnabled: site.places.feature.allowMediaReview.enabled
						};
					});
				}
			]
		}),
		elm({
			name: 'site.DataCompleteness',
			moduleName: 'DataCompletenessV2.Main',
			url: '/dataCompleteness',
			requiredPermission: 'permission/task.view',
			pageTitle: 'listings.data-completeness.title',
			argsResolver: [
				'pmtEnvService',
				'$stateParams',
				'auth',
				'pmtEnabledNetworksService',
				(pmtEnvService, $stateParams, auth, pmtEnabledNetworksService) => {
					return auth.getUser($stateParams.site).then(user => {
						const site = user.selectedSite;
						const account = {
							name: site.name,
							displayName: site.displayName,
							features: {
								allowFacebookCustomName:
									(site.places.feature.allowFacebookCustomName &&
										site.places.feature.allowFacebookCustomName.enabled) === true,
								allowGoogleCustomName:
									(site.places.feature.allowGoogleCustomName &&
										site.places.feature.allowGoogleCustomName.enabled) === true,
								allowMediaReview: site.places.feature.allowMediaReview.enabled
							},
							networks: pmtEnabledNetworksService.enabledNetworks(site)
						};
						return {
							siteName: $stateParams.site || $stateParams.siteName,
							account: account
						};
					});
				}
			]
		}),
		{
			name: 'site.NotFound',
			url: '/*rest',
			redirectTo: 'site'
		},
		elm({
			name: 'site.Conflicts',
			moduleName: 'Conflicts.Main',
			url: '/conflicts',
			requiredPermission: 'permission/task.conflict.manage',
			pageTitle: 'listings.conflicts.title',
			argsResolver: [
				'$stateParams',
				'auth',
				'pmtEnabledNetworksService',
				($stateParams, auth, pmtEnabledNetworksService) => {
					return auth.getUser($stateParams.site).then(user => {
						const site = user.selectedSite;
						const account = {
							name: site.name,
							displayName: site.displayName,
							features: {
								allowFacebookCustomName:
									(site.places.feature.allowFacebookCustomName &&
										site.places.feature.allowFacebookCustomName.enabled) === true,
								allowGoogleCustomName:
									(site.places.feature.allowGoogleCustomName &&
										site.places.feature.allowGoogleCustomName.enabled) === true
							},
							networks: pmtEnabledNetworksService.enabledNetworks(site)
						};
						return {
							siteName: $stateParams.site || $stateParams.siteName,
							account: account
						};
					});
				}
			],
			subscriptions: [
				'$timeout',
				'pmtElmService',
				'pmtAlertService',
				'$i18next',
				($timeout, pmtElmService, pmtAlertService, $i18next) => {
					return [
						{
							id: 'bulkPublishFieldConfirmAlert',
							f: function (data) {
								const conflictCount = data.count || 0;
								const conflictsString = $i18next.t('listings.tasks.conflict', {
									count: conflictCount
								});

								const body = $i18next.t('listings.tasks.bulk_publish_warning', {
									num: `<strong>${conflictCount.toString(10)}</strong>`,
									conflictsPlural: conflictsString.toLowerCase(),
									field: `<strong>${data.fieldDisplayName.toUpperCase()}</strong>`
								});

								$timeout(function () {
									pmtAlertService.alert({
										title: $i18next.t('listings.tasks.quick_resolve'),
										body: body,
										cancelTitle: $i18next.t('general.back'),
										cancel: function () {
											pmtAlertService.close();
										},
										confirmTitle: $i18next.t('listings.tasks.publish'),
										confirm: function () {
											pmtElmService.sendData(
												'Conflicts.Main',
												'in_Conflicts_Main_confirmConflictsBulkPublishField',
												{
													accountName: data.accountName,
													field: data.field
												}
											);
											pmtAlertService.close();
										}
									});
								});
							}
						}
					];
				}
			]
		}),
		elm({
			name: 'site.Examples',
			moduleName: 'Listings.Ui.Examples',
			url: '/ui/examples',
			requiredPermission: 'role/customersuccess'
		}),
		elm({
			name: 'site.LocatorLandingPage',
			moduleName: 'LocatorLandingPage.Main',
			url: '/locator-intro',
			requiredPermission: 'role/user',
			argsResolver: [
				'$stateParams',
				$stateParams => {
					return {
						listings: `/listings/${$stateParams.site}`
					};
				}
			],
			subscriptions: [
				'$stateParams',
				() => [
					{
						id: 'ToggleChat',
						f: () => {
							window.pmtCrisp?.toggle();
						}
					}
				]
			]
		}),
		elm({
			name: 'site.LocalCampaignLandingPage',
			moduleName: 'LocalCampaignLandingPage.Main',
			url: '/local-campaigns-intro',
			requiredPermission: 'role/user',
			argsResolver: [
				'$stateParams',
				$stateParams => {
					return {
						listings: `/listings/${$stateParams.site}`
					};
				}
			],
			subscriptions: [
				'$stateParams',
				() => [
					{
						id: 'ToggleChat',
						f: () => {
							window.pmtCrisp?.toggle();
						}
					}
				]
			]
		})
	];
};

module.exports = {
	getAllStates
};
